import React, { useState, useEffect } from 'react';
import Modal from '../modal/Modal';
import Button from '../common/Button';
import { ChangelogService } from '../../services/supabase/changelog.service';
import { DynamicChangelog } from '../../services/supabase/dynamic-changelog.service';
import { useAuth } from '../../context/AuthContext';

interface ChangelogPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ChangelogPopup: React.FC<ChangelogPopupProps> = ({ isOpen, onClose }) => {
  const [currentChangelog, setCurrentChangelog] = useState<DynamicChangelog | null>(null);
  const [loading, setLoading] = useState(true);
  const { markChangelogAsSeen } = useAuth();

  useEffect(() => {
    if (isOpen) {
      loadChangelog();
    }
  }, [isOpen]);

  const loadChangelog = async () => {
    try {
      setLoading(true);
      const changelog = await ChangelogService.getCurrentChangelog();
      setCurrentChangelog(changelog);
    } catch (error) {
      console.error('Erreur lors du chargement du changelog:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = async () => {
    if (currentChangelog) {
      try {
        await markChangelogAsSeen();
      } catch (error) {
        console.error('Erreur lors de la mise à jour de la version:', error);
      }
    }
    onClose();
  };

  const getFeatureIcon = (type: string) => {
    switch (type) {
      case 'feature': return '✅';
      case 'improvement': return '🚀';
      case 'bugfix': return '🐛';
      default: return '📝';
    }
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={onClose}>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Modal>
    );
  }

  if (!currentChangelog) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose}>
        <div className="text-center py-6">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
            Aucune nouveauté
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Pas de nouvelles fonctionnalités à afficher pour le moment.
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose}>
      <div className="space-y-4">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
          Nouveautés de cette version
        </h2>
        <div className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Version {currentChangelog.version} • {new Date(currentChangelog.date).toLocaleDateString('fr-FR')}
        </div>

        <div className="space-y-3 mb-8">
          {currentChangelog.features.map((feature, index) => (
            <div key={index} className="flex items-start space-x-3">
              <span className="text-lg mt-0.5">{getFeatureIcon(feature.type)}</span>
              <div>
                <h4 className="font-medium text-gray-900 dark:text-white">
                  {feature.title}
                </h4>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="flex justify-end space-x-3">
          <Button
            $variant="secondary"
            onClick={() => {
              console.log('Voir l historique complet');
            }}
            className="text-sm"
          >
            Voir l'historique complet
          </Button>
          <Button onClick={handleClose}>
            J'ai compris
          </Button>
        </div>
      </div>
    </Modal>
  );
};
